// 3D Viewer Configuration and Utilities

export const SCENE_CONFIG = {
  camera: {
    fov: 50,
    near: 0.1,
    far: 10000,
  },
  renderer: {
    clearColor: 'Gray/Light/6',
    antialias: true,
  },
  lighting: {
    hemisphere: {
      skyColor: 0xffffff,
      groundColor: 0x444444,
      intensity: 0.6,
      position: [0, 20, 0],
    },
    directional: {
      color: 0xffffff,
      intensity: 0.8,
      position: [-3, 10, 5],
      castShadow: true,
    },
  },
  controls: {
    enableDamping: true,
    dampingFactor: 0.05,
    enableZoom: true,
    enableRotate: true,
    enablePan: false,
    zoomSpeed: 0.5,
    rotateSpeed: 0.5,
    autoRotate: false,
  },
};

export const setupCameraForModel = (THREE, camera, model, controls) => {
  if (!camera || !model || !controls) return;

  const box = new THREE.Box3().setFromObject(model);
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  const maxDim = Math.max(size.x, size.y, size.z);
  const fov = camera.fov * (Math.PI / 180);
  const distance = Math.abs(maxDim / 2 / Math.tan(fov / 2));

  camera.position.set(center.x, center.y, center.z + distance * 2.5);
  camera.near = distance / 100;
  camera.far = distance * 10;
  camera.updateProjectionMatrix();
  camera.lookAt(center);

  controls.target.copy(center);
  controls.minDistance = distance * 0.5;
  controls.maxDistance = distance * 5;
};

export const createScene = (THREE) => {
  const scene = new THREE.Scene();

  const hemiLight = new THREE.HemisphereLight(
    SCENE_CONFIG.lighting.hemisphere.skyColor,
    SCENE_CONFIG.lighting.hemisphere.groundColor,
    SCENE_CONFIG.lighting.hemisphere.intensity
  );
  hemiLight.position.set(...SCENE_CONFIG.lighting.hemisphere.position);
  scene.add(hemiLight);

  const dirLight = new THREE.DirectionalLight(SCENE_CONFIG.lighting.directional.color, SCENE_CONFIG.lighting.directional.intensity);
  dirLight.position.set(...SCENE_CONFIG.lighting.directional.position);
  dirLight.castShadow = SCENE_CONFIG.lighting.directional.castShadow;
  scene.add(dirLight);

  return scene;
};

export const createCamera = (THREE, width, height) => {
  return new THREE.PerspectiveCamera(SCENE_CONFIG.camera.fov, width / height, SCENE_CONFIG.camera.near, SCENE_CONFIG.camera.far);
};

export const createRenderer = (THREE, width, height, C) => {
  const renderer = new THREE.WebGLRenderer({
    antialias: SCENE_CONFIG.renderer.antialias,
  });
  renderer.setClearColor(C(SCENE_CONFIG.renderer.clearColor));
  renderer.setSize(width, height);
  return renderer;
};

export const createControls = (OrbitControls, camera, domElement) => {
  const controls = new OrbitControls(camera, domElement);
  Object.assign(controls, SCENE_CONFIG.controls);
  return controls;
};

// Fetch the GLB ourselves and hand the ArrayBuffer to parse() instead of using
// GLTFLoader.load(). Two reasons:
//   1. GLTFLoader.load() passes a Request object to fetch(), which trips
//      Datadog's fetch instrumentation (calls `.includes()` on resource
//      expecting a string URL).
//   2. The app's fetch interceptor (reducers/utils/index.ts) injects
//      `Authorization: Bearer <token>` into every fetch unless ignoreInterceptor
//      is set. Azure Blob storage rejects SAS-signed requests that carry an
//      Authorization header (InvalidAuthenticationInfo).
export const loadGLBModel = (GLTFLoader, url, onSuccess, onError) => {
  const loader = new GLTFLoader();
  fetch(url, { ignoreInterceptor: true })
    .then(async (response) => {
      if (!response.ok) {
        const body = await response.text().catch(() => '');
        throw new Error(`GLB fetch failed: HTTP ${response.status} ${response.statusText} — ${body.slice(0, 500)}`);
      }
      return response.arrayBuffer();
    })
    .then((buffer) => loader.parse(buffer, '', onSuccess, onError))
    .catch(onError);
};
